import React, { memo, useReducer, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import Spinner1 from '../../../uiElements/spinners/spinner1/Spinner1';
import { context } from '../../../../globalState/GlobalState';
import { fetchTicketForCartAPI } from '../../../apiCalls/TicketAPICalls';
import CartRow1 from './CartRow1';
import { getLocalTimeFromDatabaseTime, getTimeInAMPMFromDate } from '../../../../catalog/DateTime';
import { getSecondaryColors } from '../../../../catalog/ColorContrast';
import { changeCartQuantity, removeTicketFromCartUsingCartItemId } from '../../../../catalog/Store';
import { showPriceFromCents } from '../../../../catalog/Price';
import { isValidObject, isValidString } from '../../../../catalog/Validity';

const initialState = {
    ticketTitle: '',
    ticketFeatureImage: '',
    ticketPrices: [],
    ticketPrice: {},
    ticketsTotal: 0,
    ticketsSold: 0,
    ticketsHold: 0,
    eventTitle: '',
    eventFeatureImage: '',
    timeOfEvent: null,
    loadingSpinner: true,
};

function reducer(state, action) {
    switch (action.type) {
        case 'fetchTicket':
            return { ...state,
                ticketTitle: action.title,
                ticketFeatureImage: action.featureImage,
                ticketPrices: action.prices,
                ticketsTotal: action.ticketsTotal,
                ticketsSold: action.ticketsSold,
                ticketsHold: action.ticketsHold,
                eventTitle: action.eventTitle,
                eventFeatureImage: action.eventFeatureImage,
                timeOfEvent: action.timeOfEvent,
            };
        case 'setPrice':
            return { ...state, ticketPrice: action.ticketPrice };
        case 'setLoadingSpinner':
            return { ...state, loadingSpinner: action.loadingSpinner };
        default:
            return initialState;
    }
}

const NavItemCartTicketItem = (props) => {
    const { cartItemId, ticketId, quantity, setPrice } = props;
    const [state, setState] = useReducer(reducer, initialState);
    const { globalState, setGlobalState } = useContext(context);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        async function fetchTicket() {
            setState({ type: 'setLoadingSpinner', loadingSpinner: true });
            await fetchTicketForCartAPI(globalState.accountId, ticketId, setState, setGlobalState, enqueueSnackbar);
            setState({ type: 'setLoadingSpinner', loadingSpinner: false });
        }

        fetchTicket();
    }, [ticketId]);

    useEffect(() => {
        setPrice(state.ticketPrice.price * quantity);
    }, [state.ticketPrice, quantity]);

    useEffect(() => {
        if (isValidString(state.ticketTitle)) {
            let priceFound = false;
            state.ticketPrices.forEach((price) => {
                if (price.id === cartItemId) {
                    priceFound = true;
                    setState({ type: 'setPrice', ticketPrice: price });
                }
            });
            if (!priceFound) {
                removeTicketFromCartUsingCartItemId(cartItemId, setGlobalState);
            }
        }
    }, [ticketId, cartItemId, state.ticketPrices]);

    const getWhen = () => {
        if (!isValidObject(state.timeOfEvent)) {
            return '';
        }
        const start = getLocalTimeFromDatabaseTime(state.timeOfEvent);
        if (start.getFullYear() === 2099 || start.getFullYear() === 2100) {
            return '';
        }
        const day = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        return start.getMinutes() === 59 && start.getSeconds() === 59 ? day : `${day} · ${getTimeInAMPMFromDate(start)}`;
    };

    const { secondary, onSecondary } = getSecondaryColors(globalState.websiteStyles.colors);

    return (
        <React.Fragment key={cartItemId}>
            {state.loadingSpinner ? (<Spinner1 color={globalState.websiteStyles.colors.primaryColor} />) : (
                <CartRow1
                    image={isValidString(state.eventFeatureImage) ? state.eventFeatureImage : state.ticketFeatureImage}
                    tag="Ticket"
                    tagStyle={{ backgroundColor: secondary, color: onSecondary }}
                    // Game titles end with " on MM-DD-YYYY"; the date has its own line.
                    title={String(state.eventTitle || state.ticketTitle || 'Ticket').replace(/\s+on\s+\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\s*$/i, '')}
                    details={[[state.ticketTitle, state.ticketPrice.title].filter((part) => { return isValidString(part); }).join(' · '), getWhen()]}
                    quantity={quantity}
                    onIncrement={() => { changeCartQuantity(cartItemId, quantity + 1, setGlobalState); }}
                    onDecrement={() => { changeCartQuantity(cartItemId, quantity - 1, setGlobalState); }}
                    onRemove={() => {
                        removeTicketFromCartUsingCartItemId(cartItemId, setGlobalState);
                        setPrice(0);
                    }}
                    lineTotal={showPriceFromCents(quantity * (state.ticketPrice.price || 0))}
                />
            )}
        </React.Fragment>
    );
};

NavItemCartTicketItem.propTypes = {
    cartItemId: PropTypes.string.isRequired,
    ticketId: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
    setPrice: PropTypes.func.isRequired,
};

export default (memo(NavItemCartTicketItem));
